import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Header } from './Header.js'
import { BottomNav } from './BottomNav.js'
import { ModeAutoHint } from './ModeAutoHint.js'
import { useObraStore } from '@stores/obraStore.js'
import { useModeStore } from '@stores/modeStore.js'
import { GUIA_ROUTE_PREFIXES, OBRA_ROUTE_PREFIXES, ROUTES } from '@utils/constants.js'
import { runAlertasProativos } from '@services/alertasReminder.js'
import { isAutoModeHintSeen, isModeExplicit, markAutoModeHintSeen } from '@utils/modePreference.js'
import logoMark from '@assets/logo-mark.svg'

const OBRA_FASES = [
  'infraestrutura',
  'superestrutura',
  'vedacao',
  'cobertura',
  'instalacoes',
  'acabamento-bruto',
  'acabamento-fino',
  'entrega',
] as const

export function AppShell() {
  const { isLoaded, isOnboarded, load, config } = useObraStore()
  const setMode = useModeStore((s) => s.setMode)
  const mode = useModeStore((s) => s.mode)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [showModeAutoHint, setShowModeAutoHint] = useState(false)

  useEffect(() => {
    load()
  }, [load])

  // Rota inequívoca (ex.: link direto, atalho da PWA) sincroniza o toggle Guia/Obra.
  useEffect(() => {
    const matches = (prefixes: readonly string[]) =>
      prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`))
    if (matches(OBRA_ROUTE_PREFIXES) && mode !== 'obra') setMode('obra')
    else if (matches(GUIA_ROUTE_PREFIXES) && mode !== 'guia') setMode('guia')
  }, [pathname, mode, setMode])

  useEffect(() => {
    if (isLoaded && !isOnboarded) {
      navigate(ROUTES.ONBOARDING, { replace: true })
    }
  }, [isLoaded, isOnboarded, navigate])

  useEffect(() => {
    if (!isLoaded || !config) return
    if (isModeExplicit()) return
    const fase = config.faseConstrutiva
    if (!fase || !OBRA_FASES.includes(fase as (typeof OBRA_FASES)[number])) return
    setMode('obra')
    if (!isAutoModeHintSeen()) {
      setShowModeAutoHint(true)
    }
  }, [isLoaded, config, setMode])

  const dismissModeAutoHint = () => {
    markAutoModeHintSeen()
    setShowModeAutoHint(false)
  }

  useEffect(() => {
    if (!showModeAutoHint || !isModeExplicit()) return
    markAutoModeHintSeen()
    setShowModeAutoHint(false)
  }, [mode, showModeAutoHint])

  useEffect(() => {
    if (!isLoaded || !isOnboarded) return
    void runAlertasProativos()
  }, [isLoaded, isOnboarded])

  if (!isLoaded) {
    return (
      <div className='cf3-splash' role='status' aria-label='Carregando...'>
        <img
          src={logoMark}
          alt=''
          className='cf3-splash__mark'
          width={64}
          height={64}
          aria-hidden
        />
        <p className='cf3-splash__title'>Minha Obra</p>
        <p className='cf3-splash__subtitle'>Carregando sua obra…</p>
      </div>
    )
  }

  return (
    <>
      <a href='#main-content' className='cf3-skip-link'>
        Pular para conteúdo principal
      </a>
      <Header />
      <main className='cf3-content' id='main-content'>
        {showModeAutoHint && <ModeAutoHint onDismiss={dismissModeAutoHint} />}
        <Outlet />
      </main>
      <BottomNav />
    </>
  )
}
